import { useCallback, useRef, useState } from 'react';
import AsyncSelect from 'react-select/async';
import { StopArea } from '@shared/models/stopArea';
import { searchStopAreas } from '@shared/utils/stopAreas';
import { getDisplayName } from '@shared/utils/stopArea';
import { SingleValue } from 'react-select';

interface StopAreaSearchProps {
  autoFocus: boolean;
  onChange: (stopArea: StopArea) => void;
}

interface StopAreaOption {
  label: string;
  value: StopArea;
}

export default function StopAreaSearch({
  autoFocus,
  onChange,
}: StopAreaSearchProps) {
  const [selectedOption, setSelectedOption] =
    useState<SingleValue<StopAreaOption>>(null);
  const abortController = useRef<AbortController | null>(null);

  const onStopAreaChange = useCallback(
    (newOption: SingleValue<StopAreaOption>) => {
      if (newOption) {
        onChange(newOption.value);
        setSelectedOption(null);
      }
    },
    [onChange],
  );

  async function getOptions(stopAreaSearch: string): Promise<StopAreaOption[]> {
    if (stopAreaSearch.trim() === '') {
      return [];
    }

    try {
      abortController.current?.abort();
      abortController.current = new AbortController();
      const newStopAreaOptions = await searchStopAreas(
        stopAreaSearch,
        abortController.current.signal,
      );

      return newStopAreaOptions.map((stopArea) => ({
        label: getDisplayName(stopArea),
        value: stopArea,
      }));
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        // Ignore automatically aborted requests
        return [];
      } else {
        throw error;
      }
    }
  }

  return (
    <div>
      <AsyncSelect
        placeholder="Sök hållplats"
        noOptionsMessage={() => 'Inga alternativ'}
        value={selectedOption}
        onChange={onStopAreaChange}
        loadOptions={getOptions}
        autoFocus={autoFocus}
      ></AsyncSelect>
    </div>
  );
}
